import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { actions } from '@/store';
import theme from '@/styles/theme';
import Message from '@/constant/message';
import { EAuthModalPage } from '@/store/types';
import validator from '@/constant/valitator';
import media from '@/styles/media';
import useSelectorUser from '@/hooks/use-selector-user';
import { Container, ContentDiv, Wrapper } from '../common/new-auth-container';
import { BasicButton, BasicButtonLabel, ButtonDiv } from '../common/new-auth-button';
import { Input } from '../common/new-auth-input';
import { Title } from '../common/new-auth-text';
import { BalloonModal } from '../common/balloon-modal';
import MobileLogo from '../common/auth-layout/mobile-logo';

export const CreatorSignUp = () => {
  const { gotoAuthLogin } = useSelectorUser('authModal');
  const [belong, setBelong] = useState<string>('');
  const [actualName, setActualName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  const [isBelong, setIsBelong] = useState<boolean>();
  const [isActualName, setIsActualName] = useState<boolean>();
  const [isPhone, setIsPhone] = useState<boolean>();

  const [belongModal, setBelongModal] = useState<string>('');
  const [nameModal, setNameModal] = useState<string>('');
  const [phoneModal, setPhoneModal] = useState<string>('');

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const validateBelong = useCallback((value: string) => {
    const { err, args } = validator.checkBelong(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const onChangeBelong = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validateBelong(value);
    setBelong(value);

    if (value.length > 0) {
      setIsBelong(valid === '');
      setBelongModal(valid);
    } else {
      setIsBelong(undefined);
    }
  }, [validateBelong]);

  const onFocusOutBelong = useCallback(() => {
    setBelongModal('');
  }, []);

  const onFocusInBelong = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validateBelong(value);
    if (valid !== '') {
      setBelongModal(valid);
    }
  }, [validateBelong]);

  const validateName = useCallback((value: string) => {
    const { err, args } = validator.checkName(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const onChangeActualName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validateName(value);
    setActualName(value);

    if (value.length > 0) {
      setIsActualName(valid === '');
    } else {
      setIsActualName(undefined);
    }

    if (valid !== '') {
      setNameModal(valid);
    }
  }, [validateName]);

  const onFocusOutActualName = useCallback(() => {
    setNameModal('');
  }, []);

  const onFocusInActualName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validateName(value);
    if (valid !== '') {
      setNameModal(valid);
    }
  }, [validateName]);

  const onChangePhone = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const number = Number(value.split('-').join(''));
    const valid = t(validator.checkPhoneNumber(value).err);
    if (!Number.isNaN(number)) {
      setPhone(number.toString());
    }
    if (value.length >= 3) {
      setIsPhone(valid === '');
      if (valid !== '') {
        setPhoneModal(valid);
      }
    } else if (value.length < 1) {
      setPhoneModal('');
      setIsPhone(undefined);
    }
  }, [t]);

  const onFocusOutPhone = useCallback(() => {
    setPhoneModal('');
  }, []);

  const onFocusInPhone = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = t(validator.checkPhoneNumber(value).err);
    if (valid !== '') {
      setPhoneModal(valid);
    }
  }, [t]);

  const gotoLogin = useCallback(() => {
    if (gotoAuthLogin) {
      gotoAuthLogin();
    }
    // dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
  }, [gotoAuthLogin]);

  const gotoRegister = useCallback(() => {
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SELECT_USER));
  }, [dispatch]);

  const onClickNext = useCallback(() => {
    dispatch(actions.user.authModal.setActualName(actualName));
    dispatch(actions.user.authModal.setBelong(belong));
    dispatch(actions.user.authModal.setPhone(phone));
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SIGN_UP));
  }, [dispatch, actualName, belong, phone]);

  const handleOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onClickNext();
    }
  }, [onClickNext]);

  return (
    <Wrapper>
      <Header>
        <BasicButtonLabel>
          {`${t(Message.이미계정이있으신가요)}`}
        </BasicButtonLabel>
        <MobileLogo />
        <BasicButton
          className="signUpPage-login"
          onClick={gotoLogin}
        >
          {t(Message.로그인)}

        </BasicButton>
      </Header>
      <ContentDiv className="teacher-signup">

        <Title>{t(Message.강사회원정보)}</Title>
        <Tag>
          <Dot />
          <TagText>{t(Message.소속)}</TagText>
        </Tag>
        <Input
          onChange={onChangeBelong}
          placeholder={t(Message.소속정보를입력해주세요)}
          validate={isBelong}
          onBlur={onFocusOutBelong}
          onFocus={onFocusInBelong}
        />
        {(belongModal !== '' && isBelong !== undefined) && <BalloonModal fade={isBelong} text={belongModal} />}
        <Tag>
          <Dot />
          <TagText>
            {t(Message.이름)}
          </TagText>
        </Tag>
        <Input
          onChange={onChangeActualName}
          placeholder={t(Message.이름을입력해주세요)}
          validate={isActualName}
          onBlur={onFocusOutActualName}
          onFocus={onFocusInActualName}
        />
        {/* 말풍선이 뜨면 UI가 아래로 밀리는 현상 발생 */}
        {/* <BalloonModal fade={false} text={nameModal} /> */}
        {(nameModal && isActualName !== undefined) && <BalloonModal fade={isActualName} text={nameModal} />}
        <Tag>
          <Dot />
          <TagText>{t(Message.연락처)}</TagText>
        </Tag>
        <Input
          onChange={onChangePhone}
          placeholder={t(Message.전화번호를입력해주세요)}
          onKeyPress={handleOnKeyPress}
          validate={isPhone}
          onBlur={onFocusOutPhone}
          onFocus={onFocusInPhone}
        />
        {(phoneModal && isPhone !== undefined) && <BalloonModal fade={isPhone} text={phoneModal} />}
        <ButtonContainer>
          <Button type="button" onClick={gotoRegister}>{t(Message.이전)}</Button>
          <NextButton
            type="button"
            onClick={onClickNext}
            disabled={!(isBelong && isActualName && isPhone)}
          >
            {t(Message.다음)}
          </NextButton>
        </ButtonContainer>
      </ContentDiv>
    </Wrapper>

  );
};

const Header = styled(ButtonDiv)`
  padding-bottom: 60px;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  padding-top: 10px;
  ${media.lg`
    width: 100%;
  `}
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  background: ${theme.color.purple};
  
`;

const TagText = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 17px;
  padding: 0 8px;
`;

const Button = styled(BasicButton)`
  flex: 0.5;
  padding: 16px;
  background-color: #4B586A;
  color: #FFFFFF;
  ${media.lg`
    flex: 1;
  `}
`;

export const NextButton = styled(Button)`
  background-color: ${theme.color.purple};
  color:#FFFFFF ;
    :disabled{
    background-color: #F0F0F0;
    color: ${theme.color.darkGray};
  }
`;

const ButtonContainer = styled(Container)`
  padding-top: 50px;
  ${media.lg`
    width: 100%;
  `}
`;
